import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, keyframes, useTheme, jsx, Global } from '@emotion/react';
import { math, rgba, rem } from 'polished';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo } from 'react';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';

var _templateObject, _templateObject2, _templateObject3;

function focusStyles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorStructureFocus = tokens.sysColorStructureFocus,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis;
  var focusInlineBorderThickness = math("".concat(sysBorderThicknessEmphasis, " + ").concat(sysBorderThicknessEmphasis));
  var focusElement = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    outline: ", " solid ", ";\n  "])), sysBorderThicknessEmphasis, sysColorStructureFocus);
  var focusOffsetElement = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    box-shadow: 0 0 0 ", " ", ";\n    outline-offset: ", ";\n    ", ";\n  "])), sysBorderThicknessEmphasis, sysColorBackgroundSurface, sysBorderThicknessEmphasis, focusElement);
  var focusInlineElement = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    box-shadow: 0 0 0 ", " inset\n      ", ";\n    outline-offset: -", ";\n    ", ";\n  "])), focusInlineBorderThickness, sysColorBackgroundSurface, sysBorderThicknessEmphasis, focusElement);
  return {
    focusElement: focusElement,
    focusOffsetElement: focusOffsetElement,
    focusInlineElement: focusInlineElement
  };
}

var _templateObject$1, _templateObject2$1, _templateObject3$1, _templateObject4;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysColorStructureShimmer = tokens.sysColorStructureShimmer,
      sysColorStructureShimmerInverse = tokens.sysColorStructureShimmerInverse,
      sysColorStructureSheen = tokens.sysColorStructureSheen,
      sysMotionDurationRepeat = tokens.sysMotionDurationRepeat;
  var animation = keyframes(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n        from {\n            background-position: 100% 0;\n        }\n        to {\n            background-position: -100% 0;\n        }\n    "])));
  var base = css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n    label: shimmer_base;\n    background-color: ", ";\n    border: ", " solid transparent;\n    background-image: linear-gradient(\n      to right,\n      ", " 0%,\n      ", " 50%,\n      ", " 100%\n    );\n    background-position: 100% 0;\n    background-size: 200%;\n    animation: ", " ", " ease-in-out infinite;\n  "])), sysColorStructureShimmer, sysBorderThicknessNormal, sysColorStructureShimmer, sysColorStructureSheen, sysColorStructureShimmer, animation, sysMotionDurationRepeat);
  var inverse = css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n    label: inverse;\n    background-color: ", ";\n    background-image: linear-gradient(\n      to right,\n      ", " 0%,\n      ", " 50%,\n      ", " 100%\n    );\n  "])), sysColorStructureShimmerInverse, sysColorStructureShimmerInverse, sysColorStructureSheen, sysColorStructureShimmerInverse);

  function defaults() {
    var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _args$borderRadius = args.borderRadius,
        borderRadius = _args$borderRadius === void 0 ? sysBorderRadiusLg : _args$borderRadius,
        _args$width = args.width,
        width = _args$width === void 0 ? '100%' : _args$width,
        _args$height = args.height,
        height = _args$height === void 0 ? '100%' : _args$height;
    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      label: default;\n      border-radius: ", ";\n      width: ", ";\n      height: ", ";\n    "])), borderRadius, width, height);
  }

  return {
    base: base,
    inverse: inverse,
    defaults: defaults
  };
}

var _templateObject$2, _templateObject2$2, _templateObject3$2, _templateObject4$1, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;

function elevationStyles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorStructureShadow = tokens.sysColorStructureShadow,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysElevationHorizontalOffsetRatio = tokens.sysElevationHorizontalOffsetRatio,
      sysElevationOpacity = tokens.sysElevationOpacity,
      sysElevationVerticalOffsetRatio = tokens.sysElevationVerticalOffsetRatio,
      sysElevationBlurCurve = tokens.sysElevationBlurCurve,
      sysElevationBlurSlope = tokens.sysElevationBlurSlope,
      sysElevationBlurBase = tokens.sysElevationBlurBase,
      sysElevationBrightenFactor = tokens.sysElevationBrightenFactor;

  function getOffsetX(level) {
    return rem(sysElevationHorizontalOffsetRatio * level);
  }

  function getOffsetY(level) {
    return rem(sysElevationVerticalOffsetRatio * level - 1);
  }

  function getBlur(level) {
    var curveAndSlopValue = rem(level * level * sysElevationBlurCurve + level * sysElevationBlurSlope);
    return math("".concat(curveAndSlopValue, " + ").concat(sysElevationBlurBase));
  }

  var color = rgba(sysColorStructureShadow, sysElevationOpacity);

  var backgroundImage = function backgroundImage(level) {
    return "background-image: linear-gradient(".concat(rgba(sysColorBackgroundSurface, level * sysElevationBrightenFactor), ", ").concat(rgba(sysColorBackgroundSurface, level * sysElevationBrightenFactor), ")");
  };

  var shadowProps = function shadowProps(level) {
    return "".concat(getOffsetX(level), " ").concat(getOffsetY(level), " ").concat(getBlur(level), " ").concat(color);
  };

  var elevation = function elevation(level) {
    return css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n    box-shadow: ", ";\n    ", ";\n  "])), shadowProps(level), backgroundImage(level));
  };

  var level1 = css(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n    label: elevation_level1;\n    ", "\n  "])), elevation(1));
  var level2 = css(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n    label: elevation_level2;\n    ", "\n  "])), elevation(2));
  var level3 = css(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n    label: elevation_level3;\n    ", "\n  "])), elevation(3));
  var filterLevel1 = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: drop_shadow1;\n    filter: drop-shadow(", ");\n  "])), shadowProps(1));
  var filterLevel2 = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: drop_shadow2;\n    filter: drop-shadow(", ");\n  "])), shadowProps(2));
  var filterLevel3 = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: drop_shadow3;\n    filter: drop-shadow(", ");\n  "])), shadowProps(3)); // NOTE: Used for fixed columns in Tables

  var level1Horizontal = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: fixed_elevation;\n    box-shadow: 0 0 ", " ", ";\n  "])), getBlur(0.33), color); // NOTE: Elevation used in Modal

  var level3Above = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: elevation_level3_above;\n    box-shadow: ", " -", " ", " ", ";\n    ", ";\n    transform: translateZ(0);\n  "])), getOffsetX(3), getOffsetY(3), getBlur(3), color, backgroundImage(3));
  return {
    level1: level1,
    level2: level2,
    level3: level3,
    level1Horizontal: level1Horizontal,
    level3Above: level3Above,
    filterLevel1: filterLevel1,
    filterLevel2: filterLevel2,
    filterLevel3: filterLevel3
  };
}

var _templateObject$3, _templateObject2$3, _templateObject3$3, _templateObject4$2, _templateObject5$1, _templateObject6$1, _templateObject7$1, _templateObject8$1;

function caretStyles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceLg = tokens.sysSpaceLg;

  var base = function base(caretSideLength) {
    return css(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n    &:before {\n      content: '';\n      position: absolute;\n      background-color: inherit;\n      width: ", ";\n      height: ", ";\n      transform: scale(0.72) rotate(45deg);\n      box-shadow: inherit;\n      z-index: 0;\n    }\n\n    //hack to cover top part of rotated skewed square and get triangle shape:\n    &:after {\n      content: '';\n      position: absolute;\n      background-color: inherit;\n      width: 2.5rem;\n      height: ", ";\n    }\n  "])), caretSideLength, caretSideLength, sysSpaceLg);
  };

  var positionBottom = function positionBottom(caretVerticalOffset) {
    return css(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n    &:before {\n      top: auto;\n      bottom: -", ";\n    }\n    &:after {\n      top: auto;\n      bottom: 0;\n    }\n  "])), caretVerticalOffset);
  };

  var positionTop = function positionTop(caretVerticalOffset) {
    return css(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteral(["\n    &:before {\n      top: -", ";\n      bottom: auto;\n    }\n    &:after {\n      top: 0;\n      bottom: auto;\n    }\n  "])), caretVerticalOffset);
  };

  var positionCenter = function positionCenter(caretWidth) {
    return css(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteral(["\n    &:before {\n      left: calc(50% - ", " / 2);\n      right: auto;\n    }\n    &:after {\n      left: calc(50% - 1.25rem);\n      right: auto;\n    }\n  "])), caretWidth);
  };

  var positionShiftLeft = function positionShiftLeft(caretSpaceHorizontal) {
    return css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n    &:before {\n      left: ", ";\n      right: auto;\n    }\n    &:after {\n      left: ", ";\n      right: auto;\n    }\n  "])), caretSpaceHorizontal, sysSpaceSm);
  };

  var positionShiftRight = function positionShiftRight(caretSpaceHorizontal) {
    return css(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n    &:before {\n      left: auto;\n      right: ", ";\n    }\n    &:after {\n      left: auto;\n      right: ", ";\n    }\n  "])), caretSpaceHorizontal, sysSpaceSm);
  };

  var positionLeft = function positionLeft(caretVerticalOffset) {
    return css(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n    &:before {\n      top: calc(50% - ", ");\n      bottom: auto;\n      left: -", ";\n      right: auto;\n    }\n    &:after {\n      transform: rotate(90deg);\n      top: calc(50% - ", ");\n      bottom: auto;\n      left: -", ";\n      right: auto;\n    }\n  "])), caretVerticalOffset, caretVerticalOffset, caretVerticalOffset, sysSpaceMd);
  };

  var positionRight = function positionRight(caretVerticalOffset) {
    return css(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteral(["\n    &:before {\n      top: calc(50% - ", ");\n      bottom: auto;\n      left: auto;\n      right: -", ";\n    }\n    &:after {\n      transform: rotate(90deg);\n      top: calc(50% - ", ");\n      bottom: auto;\n      left: auto;\n      right: -", ";\n    }\n  "])), caretVerticalOffset, caretVerticalOffset, caretVerticalOffset, sysSpaceMd);
  };

  return {
    base: base,
    positionBottom: positionBottom,
    positionTop: positionTop,
    positionCenter: positionCenter,
    positionShiftLeft: positionShiftLeft,
    positionShiftRight: positionShiftRight,
    positionLeft: positionLeft,
    positionRight: positionRight
  };
}

var AnimateDirection;

(function (AnimateDirection) {
  AnimateDirection["LEFT"] = "left";
  AnimateDirection["RIGHT"] = "right";
})(AnimateDirection || (AnimateDirection = {}));

var ZINDEX_DROPDOWN = 1060;
var ZINDEX_TOOLTIP = 1070;
var ZINDEX_COACHTIP = 1080;
var ZINDEX_MODAL = 1090; // cookiebanner z-index is 1051

var ZINDEX_TOAST = 1100; // can be on top of modal

var DEFAULT_CDNPATH = 'www.paypalobjects.com';
var DEFAULT_ASSETPATH = 'icons/v3'; // tomorrow it will be illustrations, ...

var svgBasePath = function svgBasePath() {
  var cdnHost = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_CDNPATH;
  var assetPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_ASSETPATH;
  return "https://".concat(cdnHost, "/paypal-ui/").concat(assetPath, "/svg");
};

var capitalize = function capitalize(word) {
  return word && word !== '' ? word[0].toUpperCase() + word.slice(1) : word;
};

var curry = function curry(func, firstParam) {
  return function () {
    for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
      params[_key] = arguments[_key];
    }

    return func.apply(void 0, [firstParam].concat(params));
  };
};

var getPrefixedAttribute = function getPrefixedAttribute(prefix, name, object, fallback) {
  var _ref, _object$name;

  return (_ref = (_object$name = object[name]) !== null && _object$name !== void 0 ? _object$name : object["".concat(prefix).concat(name)]) !== null && _ref !== void 0 ? _ref : typeof fallback === 'function' ? fallback(prefix, name, object) : fallback;
};

var VARIABLE_PATTERN = /\${(\w+)}/g;

var tokenTemplate = function tokenTemplate(template, tokenMap) {
  return template.replace(VARIABLE_PATTERN, function (_match, name) {
    var _tokenMap$name;

    return (_tokenMap$name = tokenMap[name]) !== null && _tokenMap$name !== void 0 ? _tokenMap$name : "${".concat(name, "}");
  });
};

var _templateObject$4, _templateObject2$4, _templateObject3$4, _templateObject4$3, _templateObject5$2, _templateObject6$2;

function variables(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysMotionTimingX1 = tokens.sysMotionTimingX1,
      sysMotionTimingY1 = tokens.sysMotionTimingY1,
      sysMotionTimingX2 = tokens.sysMotionTimingX2,
      sysMotionTimingY2 = tokens.sysMotionTimingY2;
  return {
    ppuiEaseInOut: "cubic-bezier(\n      ".concat(sysMotionTimingX1, ",\n      ").concat(sysMotionTimingY1, ",\n      ").concat(sysMotionTimingX2, ",\n      ").concat(sysMotionTimingY2, "\n    )")
  };
}

function motionTimings(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var _variables2 = variables(tokens),
      ppuiEaseInOut = _variables2.ppuiEaseInOut;

  var animationTiming = css(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n    label: motion_animation;\n    animation-timing-function: ", ";\n  "])), ppuiEaseInOut);
  var transitionTiming = css(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteral(["\n    label: motion_transition;\n    transition-timing-function: ", ";\n  "])), ppuiEaseInOut);
  return {
    animationTiming: animationTiming,
    transitionTiming: transitionTiming
  };
}

function styles$1(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var _variables = variables(tokens);

  var _focusStyles = focusStyles(tokens);

  var _shimmerStyles = styles(tokens);

  var _motionTimings = motionTimings(tokens);

  var _elevationStyles = elevationStyles(tokens);

  var _caretStyles = caretStyles(tokens);

  var screenReader = css(_templateObject3$4 || (_templateObject3$4 = _taggedTemplateLiteral(["\n    label: screenReader;\n    position: absolute;\n    clip: rect(1px, 1px, 1px, 1px);\n    padding: 0;\n    border: 0;\n    width: 1px;\n    height: 1px;\n    overflow: hidden;\n  "])));
  var ellipsis = css(_templateObject4$3 || (_templateObject4$3 = _taggedTemplateLiteral(["\n    white-space: nowrap;\n    text-overflow: ellipsis;\n    overflow: hidden;\n  "])));

  var mediaQuery = function mediaQuery(queriedCss) {
    var size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : tokens.sysSizeMediaMd;
    var styleStr = typeof queriedCss === 'string' ? queriedCss : queriedCss.styles;
    return css(_templateObject5$2 || (_templateObject5$2 = _taggedTemplateLiteral(["\n      @media screen and (max-width: ", ") {\n        ", "\n      }\n    "])), size, tokenTemplate(styleStr, tokens));
  };
  /**
   * Use direction to generate keyframes
   * @param direction AnimateDirection
   * @returns SerializedStyles
   */
  // This is tested, but coverage not registering it

  /* istanbul ignore next */


  var slideTo = function slideTo() {
    var direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : AnimateDirection.RIGHT;
    var startPosition = direction === AnimateDirection.RIGHT ? '-100%' : '100%';
    return keyframes(_templateObject6$2 || (_templateObject6$2 = _taggedTemplateLiteral(["\n        0% {\n          transform: translateX(", ");\n        }\n\n        100% {\n          transform: translateX(0);\n        }\n      "])), startPosition);
  };

  return {
    variables: _variables,
    focusStyles: _focusStyles,
    shimmerStyles: _shimmerStyles,
    motionTimings: _motionTimings,
    elevationStyles: _elevationStyles,
    caretStyles: _caretStyles,
    mediaQuery: mediaQuery,
    screenReader: screenReader,
    ellipsis: ellipsis,
    slideTo: slideTo
  };
}

var _excluded = ["as", "className", "children"];

var ScreenReaderContent = function ScreenReaderContent(_ref) {
  var _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'div' : _ref$as,
      className = _ref.className,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _useMemo = useMemo(function () {
    return styles$1(tokens);
  }, [tokens]),
      screenReader = _useMemo.screenReader;

  return jsx(as, _extends({
    css: screenReader,
    className: className
  }, rest), children);
};

var _excluded$1 = ["as", "isInverse", "className", "hasStyleDefaults", "children"];

var Shimmer = function Shimmer(_ref) {
  var _styles$defaults;

  var _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'div' : _ref$as,
      isInverse = _ref.isInverse,
      className = _ref.className,
      _ref$hasStyleDefaults = _ref.hasStyleDefaults,
      hasStyleDefaults = _ref$hasStyleDefaults === void 0 ? true : _ref$hasStyleDefaults,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var cssVal = [_styles.base, isInverse && _styles.inverse, hasStyleDefaults && ((_styles$defaults = _styles.defaults) === null || _styles$defaults === void 0 ? void 0 : _styles$defaults.call(_styles))];
  return jsx(as, _extends({
    css: cssVal,
    className: className
  }, rest), jsx(ScreenReaderContent, {
    as: "span"
  }, children));
};

var COLOR_PREFIX = 'sysColor';
var getColor = curry(getPrefixedAttribute, COLOR_PREFIX);
var mainSuffixes = {
  Main: 1,
  Surface: 1,
  Alt: 1,
  Hover: 2,
  Active: 2
};
var PARSER = /(sysColor)?([A-Z][a-z]+[0-9]?)(Main|Surface|Alt|Hover|Active|Contrast|HighContrast|MediumContrast)?/;

var getColorFamily = function getColorFamily(name, tokens) {
  var _name$;

  var parsed = name.match(PARSER);

  if (parsed === null && ((_name$ = name[0]) === null || _name$ === void 0 ? void 0 : _name$.toUpperCase()) !== name[0]) {
    return getColorFamily(capitalize(name), tokens);
  }

  var _parsed = _slicedToArray(parsed, 4),
      original = _parsed[0],
      _parsed$ = _parsed[1],
      prefix = _parsed$ === void 0 ? 'sysColor' : _parsed$,
      base = _parsed[2],
      _parsed$2 = _parsed[3],
      suffix = _parsed$2 === void 0 ? 'Main' : _parsed$2;

  var contrastName = base === 'Background' ? 'MediumContrast' : 'Contrast';
  var complement = mainSuffixes.hasOwnProperty(suffix) ? contrastName : 'Main'; // provide support for token sysColorRangeMainActive

  if (base === 'Range') {
    base = "Selected".concat(base);
  }

  var backgroundName = "".concat(prefix).concat(base).concat(suffix);
  var foregroundName = "".concat(prefix).concat(base).concat(complement);
  var altName = "".concat(prefix).concat(base, "Alt");
  var contrastHover = "".concat(foregroundName, "Hover");
  var contrastActive = "".concat(foregroundName, "Active");
  var hoverName = "".concat(prefix).concat(base).concat(suffix, "Hover");
  var activeName = "".concat(prefix).concat(base).concat(suffix, "Active");
  return {
    contrast: tokens[foregroundName],
    main: tokens[backgroundName],
    alt: tokens[altName],
    hover: tokens[hoverName],
    active: tokens[activeName],
    contrastHover: tokens[contrastHover],
    contrastActive: tokens[contrastActive],
    parsed: {
      original: original,
      base: base,
      suffix: suffix,
      prefix: prefix,
      complement: complement,
      hoverName: hoverName,
      activeName: activeName
    }
  };
};

var ColorContext = React.createContext(null);
/* istanbul ignore next */

var ColorProvider = function ColorProvider(props) {
  return /*#__PURE__*/React.createElement(ColorContext.Provider, {
    value: props.colorFamily
  }, props.children);
};
/* istanbul ignore next */


var getColorValue = function getColorValue(containerTokens, componentColor, colorType) {
  var _containerTokens$acti, _containerTokens$hove, _containerTokens$main;

  switch (colorType) {
    case 'contrast':
      return (containerTokens === null || containerTokens === void 0 ? void 0 : containerTokens.contrast) ? containerTokens.contrast : componentColor;

    case 'active':
      return (_containerTokens$acti = containerTokens === null || containerTokens === void 0 ? void 0 : containerTokens.active) !== null && _containerTokens$acti !== void 0 ? _containerTokens$acti : componentColor;

    case 'hover':
      return (_containerTokens$hove = containerTokens === null || containerTokens === void 0 ? void 0 : containerTokens.hover) !== null && _containerTokens$hove !== void 0 ? _containerTokens$hove : componentColor;

    case 'main':
      return (_containerTokens$main = containerTokens === null || containerTokens === void 0 ? void 0 : containerTokens.main) !== null && _containerTokens$main !== void 0 ? _containerTokens$main : componentColor;

    default:
      return componentColor;
  }
};

var _excluded$2 = ["level", "className", "children", "theme", "isFilter"];

var Elevation = function Elevation(_ref) {
  var level = _ref.level,
      className = _ref.className,
      children = _ref.children,
      theme = _ref.theme,
      _ref$isFilter = _ref.isFilter,
      isFilter = _ref$isFilter === void 0 ? false : _ref$isFilter,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return elevationStyles(tokens);
  }, [tokens]);

  var levelStylesMap = {
    1: _styles.level1,
    2: _styles.level2,
    3: _styles.level3
  };
  var filterStylesMap = {
    1: _styles.filterLevel1,
    2: _styles.filterLevel2,
    3: _styles.filterLevel3
  };
  return jsx("div", _extends({
    css: (isFilter ? filterStylesMap : levelStylesMap)[level],
    className: className
  }, rest), children);
};

var _templateObject$5;

function normalizeStyles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
      sysTypographyBodyFamily = tokens.sysTypographyBodyFamily,
      sysTypographyBodyStrongFamily = tokens.sysTypographyBodyStrongFamily;
  return css(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n    @font-face {\n      font-family: ", ";\n      font-style: normal;\n      font-display: swap;\n      src: url('https://www.paypalobjects.com/paypal-ui/fonts/", ".woff2')\n          format('woff2'),\n        url('https://www.paypalobjects.com/paypal-ui/fonts/", ".woff')\n          format('woff');\n    }\n\n    @font-face {\n      font-family: ", ";\n      font-style: normal;\n      font-display: swap;\n      src: url('https://www.paypalobjects.com/paypal-ui/fonts/", ".woff2')\n          format('woff2'),\n        url('https://www.paypalobjects.com/paypal-ui/fonts/", ".woff')\n          format('woff');\n    }\n\n    html {\n      box-sizing: border-box;\n    }\n\n    /* stylelint-disable selector-max-universal */\n    *,\n    *:before,\n    *:after {\n      box-sizing: inherit;\n    }\n    /* stylelint-enable selector-max-universal */\n\n    /* Setting these elements to height of 100% ensures that\n    * .pp_react_foreground-container fully covers the whole viewport\n    */\n    html,\n    body {\n      height: 100%;\n      font-family: ", ", 'Helvetica Neue', Arial,\n        sans-serif;\n      color: ", ";\n    }\n\n    /* End of core global style */\n\n    /* normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */\n\n    /* Document\n      ========================================================================== */\n\n    /**\n    * 1. Correct the line height in all browsers.\n    * 2. Prevent adjustments of font size after orientation changes in iOS.\n    */\n\n    /* stylelint-disable no-duplicate-selectors */\n    html {\n      line-height: 1.15; /* 1 */\n      -webkit-text-size-adjust: 100%; /* 2 */\n    }\n    /* stylelint-enable no-duplicate-selectors */\n\n    /* Sections\n      ========================================================================== */\n\n    /**\n    * Remove the margin in all browsers.\n    */\n\n    body {\n      margin: 0;\n      -webkit-font-smoothing: antialiased;\n      -moz-osx-font-smoothing: grayscale;\n    }\n\n    /**\n    * Render the 'main' element consistently in IE.\n    */\n\n    main {\n      display: block;\n    }\n\n    /**\n    * Correct the font size and margin on 'h1' elements within 'section' and\n    * 'article' contexts in Chrome, Firefox, and Safari.\n    */\n\n    h1 {\n      font-size: 2em;\n      margin: 0.67em 0;\n    }\n\n    /* Grouping content\n      ========================================================================== */\n\n    /**\n    * 1. Add the correct box sizing in Firefox.\n    * 2. Show the overflow in Edge and IE.\n    */\n\n    hr {\n      box-sizing: content-box; /* 1 */\n      height: 0; /* 1 */\n      overflow: visible; /* 2 */\n    }\n\n    /**\n    * 1. Correct the inheritance and scaling of font size in all browsers.\n    * 2. Correct the odd 'em' font sizing in all browsers.\n    */\n\n    pre {\n      font-family: monospace; /* 1 */\n      font-size: 1em; /* 2 */\n    }\n\n    /* Text-level semantics\n      ========================================================================== */\n\n    /**\n    * Remove the gray background on active links in IE 10.\n    */\n\n    a {\n      background-color: transparent;\n    }\n\n    /**\n    * 1. Remove the bottom border in Chrome 57-\n    * 2. Add the correct text decoration in Chrome, Edge, IE, Opera, and Safari.\n    */\n\n    /* stylelint-disable selector-no-qualifying-type */\n    abbr[title] {\n      border-bottom: none; /* 1 */\n      text-decoration: underline; /* 2 */\n      text-decoration: underline dotted; /* 2 */\n    }\n    /* stylelint-enable selector-no-qualifying-type */\n\n    /**\n    * Add the correct font weight in Chrome, Edge, and Safari.\n    */\n\n    /* stylelint-disable font-weight-notation */\n    b,\n    strong {\n      font-weight: bolder;\n    }\n    /* stylelint-enable font-weight-notation */\n\n    /**\n    * 1. Correct the inheritance and scaling of font size in all browsers.\n    * 2. Correct the odd 'em' font sizing in all browsers.\n    */\n\n    code,\n    kbd,\n    samp {\n      font-family: monospace; /* 1 */\n      font-size: 1em; /* 2 */\n    }\n\n    /**\n    * Add the correct font size in all browsers.\n    */\n\n    small {\n      font-size: 80%;\n    }\n\n    /**\n    * Prevent 'sub' and 'sup' elements from affecting the line height in\n    * all browsers.\n    */\n\n    sub,\n    sup {\n      font-size: 75%;\n      line-height: 0;\n      position: relative;\n      vertical-align: baseline;\n    }\n\n    sub {\n      bottom: -0.25em;\n    }\n\n    sup {\n      top: -0.5em;\n    }\n\n    /* Embedded content\n      ========================================================================== */\n\n    /**\n    * Remove the border on images inside links in IE 10.\n    */\n\n    img {\n      border-style: none;\n    }\n\n    /* Forms\n      ========================================================================== */\n\n    /**\n    * 1. Change the font styles in all browsers.\n    * 2. Remove the margin in Firefox and Safari.\n    */\n\n    button,\n    input,\n    optgroup,\n    select,\n    textarea {\n      font-family: inherit; /* 1 */\n      font-size: 100%; /* 1 */\n      line-height: 1.15; /* 1 */\n      margin: 0; /* 2 */\n    }\n\n    /**\n    * Show the overflow in IE.\n    * 1. Show the overflow in Edge.\n    */\n\n    button,\n    input {\n      /* 1 */\n      overflow: visible;\n    }\n\n    /**\n    * Remove the inheritance of text transform in Edge, Firefox, and IE.\n    * 1. Remove the inheritance of text transform in Firefox.\n    */\n\n    button,\n    select {\n      /* 1 */\n      text-transform: none;\n    }\n\n    /**\n    * Correct the inability to style clickable types in iOS and Safari.\n    */\n\n    button,\n    [type='button'],\n    [type='reset'],\n    [type='submit'] {\n      -webkit-appearance: button;\n    }\n\n    /**\n    * Remove the inner border and padding in Firefox.\n    */\n\n    button::-moz-focus-inner,\n    [type='button']::-moz-focus-inner,\n    [type='reset']::-moz-focus-inner,\n    [type='submit']::-moz-focus-inner {\n      border-style: none;\n      padding: 0;\n    }\n\n    /**\n    * Restore the focus styles unset by the previous rule.\n    */\n\n    button:-moz-focusring,\n    [type='button']:-moz-focusring,\n    [type='reset']:-moz-focusring,\n    [type='submit']:-moz-focusring {\n      outline: 1px dotted ButtonText;\n    }\n\n    /**\n    * Correct the padding in Firefox.\n    */\n\n    fieldset {\n      padding: 0.35em 0.75em 0.625em;\n    }\n\n    /**\n    * 1. Correct the text wrapping in Edge and IE.\n    * 2. Correct the color inheritance from 'fieldset' elements in IE.\n    * 3. Remove the padding so developers are not caught out when they zero out\n    *    'fieldset' elements in all browsers.\n    */\n\n    legend {\n      box-sizing: border-box; /* 1 */\n      color: inherit; /* 2 */\n      display: table; /* 1 */\n      max-width: 100%; /* 1 */\n      padding: 0; /* 3 */\n      white-space: normal; /* 1 */\n    }\n\n    /**\n    * Add the correct vertical alignment in Chrome, Firefox, and Opera.\n    */\n\n    progress {\n      vertical-align: baseline;\n    }\n\n    /**\n    * Remove the default vertical scrollbar in IE 10+.\n    */\n\n    textarea {\n      overflow: auto;\n    }\n\n    /**\n    * 1. Add the correct box sizing in IE 10.\n    * 2. Remove the padding in IE 10.\n    */\n\n    [type='checkbox'],\n    [type='radio'] {\n      box-sizing: border-box; /* 1 */\n      padding: 0; /* 2 */\n    }\n\n    /**\n    * Correct the cursor style of increment and decrement buttons in Chrome.\n    */\n\n    [type='number']::-webkit-inner-spin-button,\n    [type='number']::-webkit-outer-spin-button {\n      height: auto;\n    }\n\n    /**\n    * 1. Correct the odd appearance in Chrome and Safari.\n    * 2. Correct the outline style in Safari.\n    */\n\n    [type='search'] {\n      -webkit-appearance: textfield; /* 1 */\n      outline-offset: -2px; /* 2 */\n    }\n\n    /**\n    * Remove the inner padding in Chrome and Safari on macOS.\n    */\n\n    [type='search']::-webkit-search-decoration {\n      -webkit-appearance: none;\n    }\n\n    /**\n    * 1. Correct the inability to style clickable types in iOS and Safari.\n    * 2. Change font properties to 'inherit' in Safari.\n    */\n\n    ::-webkit-file-upload-button {\n      -webkit-appearance: button; /* 1 */\n      font: inherit; /* 2 */\n    }\n\n    /* Interactive\n      ========================================================================== */\n\n    /*\n    * Add the correct display in Edge, IE 10+, and Firefox.\n    */\n\n    details {\n      display: block;\n    }\n\n    /*\n    * Add the correct display in all browsers.\n    */\n\n    summary {\n      display: list-item;\n    }\n\n    /* Misc\n      ========================================================================== */\n\n    /**\n    * Add the correct display in IE 10+.\n    */\n\n    template {\n      display: none;\n    }\n\n    /**\n    * Add the correct display in IE 10.\n    */\n\n    [hidden] {\n      display: none;\n    }\n\n    /* stylelint-enable property-no-vendor-prefix */\n  "])), sysTypographyBodyFamily, sysTypographyBodyFamily, sysTypographyBodyFamily, sysTypographyBodyStrongFamily, sysTypographyBodyStrongFamily, sysTypographyBodyStrongFamily, sysTypographyBodyFamily, sysColorBackgroundHighContrast);
}

var _excluded$3 = ["as", "children"];

var FontsAndNormalize = function FontsAndNormalize(_ref) {
  var _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'div' : _ref$as,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$3);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return normalizeStyles(tokens);
  }, [tokens]);

  return jsx(as, rest, jsx(Global, {
    styles: _styles
  }), children);
};

var UNIT_PARSER = /([+-]?\d*\.?\d*)([a-z%]*)?/;

var splitValue = function splitValue(str, defaultUnit) {
  var _ref;

  if (typeof str === 'number') {
    return {
      unit: defaultUnit,
      value: str
    };
  }

  var parts = str.match(UNIT_PARSER);
  var value = parseFloat(str);
  return {
    value: value,
    unit: (_ref = parts && parts[2]) !== null && _ref !== void 0 ? _ref : defaultUnit
  };
};

var getMs = function getMs(str) {
  var _splitValue = splitValue(str, 'ms'),
      unit = _splitValue.unit,
      value = _splitValue.value;

  return unit === 's' ? value * 1000 : value;
};

var getPx = function getPx(str) {
  var remBase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 16;

  var _splitValue2 = splitValue(str, 'px'),
      unit = _splitValue2.unit,
      value = _splitValue2.value;

  return unit === 'rem' ? value * remBase : value;
};

var getRem = function getRem(str) {
  var remBase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 16;

  var _splitValue3 = splitValue(str, 'rem'),
      unit = _splitValue3.unit,
      value = _splitValue3.value;

  return unit === 'px' ? value / remBase : value;
};
/**
 * Check whether window object is present or not.
 * @returns boolean
 */


var hasWindow = function hasWindow() {
  return typeof window !== 'undefined';
};
/**
 * Check whether navigator object is present or not.
 * @returns boolean
 */

/* istanbul ignore next */


var hasNavigator = function hasNavigator() {
  return typeof navigator !== 'undefined';
};
/**
 * Check whether document object is present or not.
 * @returns boolean
 */

/* istanbul ignore next */


var hasDocument = function hasDocument() {
  return typeof document !== 'undefined';
};
/**
 * Check whether the browser is running in a touch device or not.
 * @returns boolean
 */

/* istanbul ignore next */


var isTouchDevice = function isTouchDevice() {
  var _navigator$maxTouchPo;

  if (hasWindow() && 'ontouchstart' in window) {
    return true;
  }

  return !!(hasNavigator() && ((_navigator$maxTouchPo = navigator.maxTouchPoints) !== null && _navigator$maxTouchPo !== void 0 ? _navigator$maxTouchPo : navigator.msMaxTouchPoints));
};
/**
 * Check whether the names passed as param is the active element or not.
 * @param elementName Names of the active element. For example `INPUT`, `TEXTAREA`, etc.
 * @returns boolean
 */

/* istanbul ignore next */


var isActiveElementIn = function isActiveElementIn(elementNames) {
  if (!hasDocument()) {
    return false;
  }

  var activeElement = document.activeElement;
  return activeElement && elementNames.includes(activeElement.tagName);
};
/**
 * Check whether the browser document's direction attribute is `rtl` or not.
 * @returns boolean
 */

/* istanbul ignore next */


var isDocumentRTL = function isDocumentRTL() {
  return hasWindow() ? getComputedStyle(document.body).direction === 'rtl' : false;
};

var _excluded$4 = ["className"],
    _excluded2 = ["className"];

function useMotionCSS() {
  var tokens = useTheme();

  var _useMemo = useMemo(function () {
    return styles$1(tokens);
  }, [tokens]),
      _useMemo$motionTiming = _useMemo.motionTimings,
      animationTiming = _useMemo$motionTiming.animationTiming,
      transitionTiming = _useMemo$motionTiming.transitionTiming;

  return {
    animationTiming: animationTiming,
    transitionTiming: transitionTiming
  };
}

var withAnimation = function withAnimation(WrappedComponent) {
  var WCAnimation = function WCAnimation(_ref) {
    var className = _ref.className,
        rest = _objectWithoutProperties(_ref, _excluded$4);

    return jsx(WrappedComponent, _extends({
      css: useMotionCSS().animationTiming,
      className: className
    }, rest));
  };

  WCAnimation.displayName = 'withAnimation';
  return WCAnimation;
};

var withTransition = function withTransition(WrappedComponent) {
  var WCTransition = function WCTransition(_ref2) {
    var className = _ref2.className,
        rest = _objectWithoutProperties(_ref2, _excluded2);

    return jsx(WrappedComponent, _extends({
      css: useMotionCSS().transitionTiming,
      className: className
    }, rest));
  };

  WCTransition.displayName = 'withTransition';
  return WCTransition;
};

var PREFIX_DEFAULT = 'ppui';
var ppuiUniqueIdCounter = {};
/**
 * Generates an unique ID.
 * If prefix is passed, the function prepends the prefix with the ID.
 *
 * @param {string} prefix The value to prefix with the ID.
 * @returns {string} The unique ID.
 */

function uniqueId() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : PREFIX_DEFAULT;

  if (!ppuiUniqueIdCounter[prefix]) {
    ppuiUniqueIdCounter[prefix] = 0;
  }

  var id = ++ppuiUniqueIdCounter[prefix];
  return prefix === PREFIX_DEFAULT ? "".concat(id) : "".concat(prefix).concat(id);
}

var KeyboardCode = /*#__PURE__*/function () {
  function KeyboardCode(code) {
    _classCallCheck(this, KeyboardCode);

    _defineProperty(this, "key", void 0);

    this.key = code;
  }

  _createClass(KeyboardCode, [{
    key: "IS_ARROW_LEFT",
    get: function get() {
      return ['ArrowLeft', KeyboardCode.NAME_ARROW_LEFT, KeyboardCode.CODE_ARROW_LEFT].includes(this.key);
    }
  }, {
    key: "IS_ARROW_RIGHT",
    get: function get() {
      return ['ArrowRight', KeyboardCode.NAME_ARROW_RIGHT, KeyboardCode.CODE_ARROW_RIGHT].includes(this.key);
    }
  }, {
    key: "IS_ARROW_UP",
    get: function get() {
      return ['ArrowUp', KeyboardCode.NAME_ARROW_UP, KeyboardCode.CODE_ARROW_UP].includes(this.key);
    }
  }, {
    key: "IS_ARROW_DOWN",
    get: function get() {
      return ['ArrowDown', KeyboardCode.NAME_ARROW_DOWN, KeyboardCode.CODE_ARROW_DOWN].includes(this.key);
    }
  }, {
    key: "IS_ENTER",
    get: function get() {
      return [KeyboardCode.NAME_ENTER, KeyboardCode.CODE_ENTER].includes(this.key);
    }
  }, {
    key: "IS_SPACEBAR",
    get: function get() {
      return [KeyboardCode.NAME_SPACE, KeyboardCode.CODE_SPACE].includes(this.key);
    }
  }, {
    key: "IS_TAB",
    get: function get() {
      return [KeyboardCode.NAME_TAB, KeyboardCode.CODE_TAB].includes(this.key);
    }
  }, {
    key: "IS_ESC",
    get: function get() {
      return ['Escape', KeyboardCode.NAME_ESC, KeyboardCode.CODE_ESC].includes(this.key);
    }
  }, {
    key: "IS_HOME",
    get: function get() {
      return [KeyboardCode.NAME_HOME, KeyboardCode.CODE_HOME].includes(this.key);
    }
  }, {
    key: "IS_END",
    get: function get() {
      return [KeyboardCode.NAME_END, KeyboardCode.CODE_END].includes(this.key);
    }
  }, {
    key: "IS_BACKSPACE",
    get: function get() {
      return [KeyboardCode.NAME_BACKSPACE, KeyboardCode.CODE_BACKSPACE].includes(this.key);
    }
  }, {
    key: "IS_DELETE",
    get: function get() {
      return [KeyboardCode.NAME_DELETE, KeyboardCode.CODE_DELETE].includes(this.key);
    }
  }, {
    key: "IS_SIDE_NAVIGATION",
    get: function get() {
      return this.IS_ARROW_LEFT || this.IS_ARROW_RIGHT || this.IS_END || this.IS_HOME;
    }
  }, {
    key: "IS_ARROW_KEY",
    get: function get() {
      return this.IS_ARROW_LEFT || this.IS_ARROW_RIGHT || this.IS_ARROW_UP || this.IS_ARROW_DOWN;
    }
  }]);

  return KeyboardCode;
}();

_defineProperty(KeyboardCode, "CODE_ARROW_UP", 38);

_defineProperty(KeyboardCode, "CODE_ARROW_DOWN", 40);

_defineProperty(KeyboardCode, "CODE_ARROW_LEFT", 37);

_defineProperty(KeyboardCode, "CODE_ARROW_RIGHT", 39);

_defineProperty(KeyboardCode, "CODE_ENTER", 13);

_defineProperty(KeyboardCode, "CODE_SPACE", 32);

_defineProperty(KeyboardCode, "CODE_ESC", 27);

_defineProperty(KeyboardCode, "CODE_TAB", 9);

_defineProperty(KeyboardCode, "CODE_CAPS_LOCK", 20);

_defineProperty(KeyboardCode, "CODE_HOME", 36);

_defineProperty(KeyboardCode, "CODE_END", 35);

_defineProperty(KeyboardCode, "CODE_BACKSPACE", 8);

_defineProperty(KeyboardCode, "CODE_DELETE", 46);

_defineProperty(KeyboardCode, "NAME_ARROW_UP", 'Up');

_defineProperty(KeyboardCode, "NAME_ARROW_DOWN", 'Down');

_defineProperty(KeyboardCode, "NAME_ARROW_LEFT", 'Left');

_defineProperty(KeyboardCode, "NAME_ARROW_RIGHT", 'Right');

_defineProperty(KeyboardCode, "NAME_ENTER", 'Enter');

_defineProperty(KeyboardCode, "NAME_SPACE", ' ');

_defineProperty(KeyboardCode, "NAME_ESC", 'Esc');

_defineProperty(KeyboardCode, "NAME_TAB", 'Tab');

_defineProperty(KeyboardCode, "NAME_HOME", 'Home');

_defineProperty(KeyboardCode, "NAME_END", 'End');

_defineProperty(KeyboardCode, "NAME_BACKSPACE", 'Backspace');

_defineProperty(KeyboardCode, "NAME_DELETE", 'Delete');

var devWarning = function devWarning(warningText) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(warningText);
  }
};

export { AnimateDirection, COLOR_PREFIX, ColorContext, ColorProvider, DEFAULT_ASSETPATH, DEFAULT_CDNPATH, Elevation, FontsAndNormalize, KeyboardCode, ScreenReaderContent, Shimmer, ZINDEX_COACHTIP, ZINDEX_DROPDOWN, ZINDEX_MODAL, ZINDEX_TOAST, ZINDEX_TOOLTIP, capitalize, curry, devWarning, getColor, getColorFamily, getColorValue, getMs, getPrefixedAttribute, getPx, getRem, hasDocument, hasNavigator, hasWindow, isActiveElementIn, isDocumentRTL, isTouchDevice, splitValue, svgBasePath, tokenTemplate, uniqueId, styles$1 as utilStyles, withAnimation, withTransition };